import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatGBP } from "@/lib/api";
import { cardTotal, computedTotal, totalMismatch, refundTotal, detectedLayout, layoutFor, refundSplitMismatch } from "@/lib/z";
import { Plus, X, AlertTriangle } from "lucide-react";

const Money = ({ label, hint, value, onChange, testid }) => (
  <div className="space-y-1">
    <Label className="text-sm font-medium text-navy">{label}</Label>
    <Input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" className="gm-input font-mono" data-testid={testid} />
    {hint && <p className="text-[11px] text-navy/45">{hint}</p>}
  </div>
);

function Rows({ rows, onChange, addLabel, prefix, namePh }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder={namePh} className="gm-input flex-1" data-testid={`${prefix}-name-${i}`} />
          <Input inputMode="decimal" value={r.amount} onChange={(e) => upd(i, "amount", e.target.value)} placeholder="0.00" className="gm-input font-mono w-28" data-testid={`${prefix}-amount-${i}`} />
          <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40" aria-label="Remove" data-testid={`${prefix}-remove-${i}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, { name: "", amount: "" }])} className="text-sm text-navy/70 flex items-center gap-1 min-h-[44px]" data-testid={`${prefix}-add-btn`}><Plus className="w-4 h-4" /> {addLabel}</button>
    </div>
  );
}

function CardSection({ form, set }) {
  const mode = form.card_mode;
  return (
    <div className="space-y-2 rounded-xl border border-navy/10 bg-white p-3" data-testid="card-section">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-navy">Card sales</Label>
        <div className="flex rounded-full border border-navy/15 overflow-hidden text-xs">
          {[["combined", "One total"], ["machines", "Per machine"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set("card_mode", v)} data-testid={`card-mode-${v}`}
              className={`px-3 min-h-[36px] ${mode === v ? "bg-navy text-white" : "text-navy"}`}>{l}</button>
          ))}
        </div>
      </div>
      {mode === "combined" ? (
        <Input inputMode="decimal" value={form.card_sales} onChange={(e) => set("card_sales", e.target.value)} placeholder="0.00" className="gm-input font-mono" data-testid="z-card-input" />
      ) : (
        <>
          <Rows rows={form.card_machines} onChange={(v) => set("card_machines", v)} addLabel="Add card machine" prefix="card-machine" namePh="Machine name" />
          <p className="text-sm text-navy">Combined card total: <span className="font-mono font-semibold" data-testid="z-card-combined">{formatGBP(cardTotal(form))}</span></p>
        </>
      )}
      <p className="text-[11px] text-navy/45">Card takings as printed on the Z report. Bank settlement is recorded separately.</p>
    </div>
  );
}

function Refunds({ form, set }) {
  const any = refundTotal(form) > 0;
  const auto = detectedLayout(form);
  const layout = layoutFor(form);
  return (
    <div className="space-y-3 rounded-xl border border-navy/10 bg-white p-3" data-testid="refunds-section">
      <Label className="text-sm font-medium text-navy">Refunds by payment method</Label>
      {form.refunds_printed !== "" && (
        <p className="text-xs text-amber-800" data-testid="z-refunds-printed">The Z report shows {formatGBP(form.refunds_printed)} refunds in total. Enter how much was given back in cash, and the rest by card or other.</p>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Money label="Cash" value={form.cash_refunds} onChange={(v) => set("cash_refunds", v)} testid="z-cash-refunds-input" />
        <Money label="Card" value={form.card_refunds} onChange={(v) => set("card_refunds", v)} testid="z-card-refunds-input" />
        <Money label="Other" value={form.other_refunds} onChange={(v) => set("other_refunds", v)} testid="z-other-refunds-input" />
      </div>
      <p className="text-[11px] text-navy/45">Only cash refunds come out of the till. Card refunds go back to the customer's card.</p>
      {refundSplitMismatch(form) && <p className="text-xs text-red-700" data-testid="z-refund-split-mismatch">These add up to {formatGBP(refundTotal(form))}, not {formatGBP(form.refunds_printed)}.</p>}
      {any && (
        <div className="space-y-1.5" data-testid="refund-layout">
          <p className="text-xs text-navy">On this Z report, are the cash and card figures before or after refunds?{auto && !form.refund_layout ? ` (matches the printed total: ${auto === "net" ? "after" : "before"})` : ""}</p>
          <div className="grid grid-cols-2 gap-2">
            {[["gross", "Before refunds"], ["net", "After refunds"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("refund_layout", form.refund_layout === v ? "" : v)} data-testid={`refund-layout-${v}`}
                className={`min-h-[40px] rounded-lg border text-xs ${layout === v ? "bg-navy text-white border-navy" : "border-navy/15 text-navy"}`}>{l}</button>
            ))}
          </div>
          {!layout && <p className="text-xs text-amber-700" data-testid="refund-layout-needed">Enter the printed total or choose one so refunds are not taken off twice.</p>}
        </div>
      )}
    </div>
  );
}

export default function ZAmounts({ form, set }) {
  const total = computedTotal(form);
  return (
    <div className="space-y-4" data-testid="z-amounts">
      <Money label="Cash sales" value={form.cash_sales} onChange={(v) => set("cash_sales", v)} testid="z-cash-input" />
      <CardSection form={form} set={set} />
      <Money label="Account / credit sales" value={form.account_sales} onChange={(v) => set("account_sales", v)} testid="z-account-input"
        hint="Sales put on customer accounts today. Money customers paid off their account is NOT a sale — record it under Payments as a customer collection." />
      <Money label="Bank transfer sales" value={form.bank_transfer_sales} onChange={(v) => set("bank_transfer_sales", v)} testid="z-bank-input" />
      <div className="space-y-1">
        <Label className="text-sm font-medium text-navy">Other tenders</Label>
        <Rows rows={form.other_tenders} onChange={(v) => set("other_tenders", v)} addLabel="Add other tender (voucher, PayPoint…)" prefix="other-tender" namePh="Tender name" />
      </div>
      <Refunds form={form} set={set} />
      <Money label="Adjustments (±)" value={form.adjustments} onChange={(v) => set("adjustments", v)} testid="z-adjustments-input" />
      <div className="rounded-xl bg-navy text-white p-4 space-y-3">
        <div className="flex justify-between text-sm"><span className="text-white/70">Calculated total</span><span className="font-mono font-bold text-lg" data-testid="z-computed-total">{formatGBP(total)}</span></div>
        <div className="space-y-1">
          <Label className="text-xs text-white/70">Total sales printed on the Z report</Label>
          <Input inputMode="decimal" value={form.z_total} onChange={(e) => set("z_total", e.target.value)} placeholder={total.toFixed(2)} className="gm-input font-mono text-navy bg-white" data-testid="z-total-input" />
        </div>
        {totalMismatch(form) && (
          <p className="text-xs text-amber-300 flex gap-1" data-testid="z-total-mismatch"><AlertTriangle className="w-4 h-4 shrink-0" /> The lines add up to {formatGBP(total)} but the Z report says {formatGBP(form.z_total)}. Check each figure.</p>
        )}
      </div>
    </div>
  );
}
